import { InvestorDetailsGetInvestorLegalEntitiesQuery } from '@/__generated__/graphql';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { DeleteBucketIcon } from '@/assets/static/icons/delete-bucket';
import { Banner } from '@/components/basicComponents/banner';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import { ISelectValue } from '@/components/basicComponents/select';
import { useErrorMessages } from '@/hooks/i18n';
import { useGoBack } from '@/hooks/useGoBack';
import { themeVar } from '@/styles/theme';
import { ApolloQueryResult, useMutation } from '@apollo/client';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { DELETE_LEGAL_ENTITY_MUTATION } from '../../queries';
import EditInvestorDeleteHouseholdDialog from './deleteHousehold';
import EditInvestorSuccessDialog from './success';

enum DialogTypeEnum {
  DeleteHousehold = 'delete-household',
  Loading = 'loading',
  Success = 'success'
}

interface DeleteEntityProps {
  isOpen: boolean;
  entityName: string;
  entitiesSelectOptions: ISelectValue[];
  investorLegalEntitiesRefetch: () => Promise<ApolloQueryResult<InvestorDetailsGetInvestorLegalEntitiesQuery>>;
  onClose: () => void;
}

const DeleteEntity = ({ isOpen, entityName, entitiesSelectOptions, investorLegalEntitiesRefetch, onClose }: DeleteEntityProps) => {
  const goBack = useGoBack();
  const params = useParams();
  const navigate = useNavigate();
  const { default: defaultErrorMessage } = useErrorMessages();

  const investorId = params?.investorId || '';
  const entityId = params?.entityId || '';

  const isLastEntity = entitiesSelectOptions?.length === 1;

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [dialogType, setDialogType] = useState<DialogTypeEnum | null>(null);

  const [deleteLegalEntity] = useMutation(DELETE_LEGAL_ENTITY_MUTATION);

  const navigateToEditInvestorPage = () => {
    if (isLastEntity) {
      goBack({ fallBack: '/investors', goBackSteps: 2 });
      return;
    }
    if (entitiesSelectOptions?.length === 2) {
      const entityIdToNavigate = entitiesSelectOptions?.find((entity) => entity.value !== entityId)?.value;
      investorLegalEntitiesRefetch();
      navigate(`/investors/${investorId}/${entityIdToNavigate}/edit`, { replace: true });
      onClose();
      return;
    }
    investorLegalEntitiesRefetch();
    navigate(`/investors/${investorId}/edit`, { replace: true });
    onClose();
  };

  const handleDeleteEntity = async () => {
    try {
      setDialogType(DialogTypeEnum.Loading);

      await deleteLegalEntity({
        variables: {
          id: entityId
        }
      });

      setDialogType(DialogTypeEnum.Success);
    } catch (error) {
      setErrorMessage(defaultErrorMessage);
      setDialogType(null);
    }
  };

  const onConfirm = () => {
    if (isLastEntity) {
      setDialogType(DialogTypeEnum.DeleteHousehold);
      return;
    }
    handleDeleteEntity();
  };

  if (dialogType === DialogTypeEnum.Loading) {
    return (
      <ModalWrappedContent isOpen={true} title="Saving" showCancelButton={false} showCloseIcon={false} onClose={() => {}}>
        <ModalLoader />
      </ModalWrappedContent>
    );
  }

  if (dialogType === DialogTypeEnum.Success) {
    return (
      <EditInvestorSuccessDialog
        isOpen={isOpen}
        description={
          <>
            <b>{entityName || ''}</b> has been deleted.
          </>
        }
        onClose={navigateToEditInvestorPage}
      />
    );
  }

  if (dialogType === DialogTypeEnum.DeleteHousehold) {
    return <EditInvestorDeleteHouseholdDialog isOpen={isOpen} entityName={entityName} onConfirm={handleDeleteEntity} onClose={onClose} />;
  }

  return (
    <ModalWrappedContent
      isOpen={isOpen}
      onClose={onClose}
      confirmButton={{
        name: 'Yes, Delete Forever',
        onClick: onConfirm
      }}
      cancelButtonName="No, Cancel and Do Not Delete"
    >
      <ModalBody>
        <DeleteBucketIcon fill={themeVar('font.disabled')} />
        <ModalTitle>Please confirm you want to delete this Entity</ModalTitle>
        {errorMessage && (
          <ErrorBannerWrapper>
            <Banner
              icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
              title="Error"
              description={errorMessage}
              bgColor={themeVar('context.error')}
            />
          </ErrorBannerWrapper>
        )}
        <ModalDescription>
          This action will delete <b>{entityName || ''}</b> and all associated data. This action cannot be undone. Are you absolutely sure that you
          want to proceed?
        </ModalDescription>
      </ModalBody>
    </ModalWrappedContent>
  );
};

export default DeleteEntity;

const ModalBody = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;
`;

const ModalTitle = styled.div`
  font-family: Blinker;
  font-size: 33px;
  font-style: normal;
  font-weight: 400;
  line-height: 46.2px;
  text-align: center;
  color: ${themeVar('font.strong')};
`;

const ModalDescription = styled.div`
  font-size: 16px;
  font-style: normal;
  text-align: center;
  color: ${themeVar('font.base')};
`;

const ErrorBannerWrapper = styled.div`
  margin-bottom: 10px;
`;
